import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
//import 'bootstrap/dist/css/bootstrap.min.css';

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface Translations {
  typingIndicator: string;
  chatError: string;
  tryAgain: string;
  title: string;
  intro: string;
  inputPlaceholder: string;
}

interface Skill {
  name: string;
  help_text: string;
}

interface ChatOverlayProps {
  translations: Translations;
  skill: Skill;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({ translations, skill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [chatbotThreadId, setChatbotThreadId] = useState<string | null>(null);

  const messageUrl = skill?.name ? '/chatbot/skillcheck' : '/chatbot/help';


  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add initial bot message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const initialMessage: ChatMessage = {
        id: 'initial',
        content: translations.intro,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([initialMessage]);
    }

  }, [isOpen, messages.length, translations.intro]);

  const addMessage = (content: string, sender: 'user' | 'bot') => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      sender,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    if (!message || isLoading) return;

    // Add user message
    addMessage(message, 'user');
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(messageUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ message, skill_name: skill?.name, skill_help: skill?.help_text, thread_id: chatbotThreadId })
      });

      if (response.ok) {
        const data = await response.json();
        addMessage(data.answer, 'bot');
        setChatbotThreadId(data.thread_id);
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (err) {
      setError(translations.chatError);
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = async () => {
    try {
      await fetch('/chatbot/reset', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
    } catch (err) {
      console.error('Reset error:', err);
    }
  };

  const handleClose = () => {
    setMessages([]);
    setInputValue('');
    setError(null);
    setIsLoading(false);
    resetChat();
    setIsOpen(false);
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(true);
  };

  return (
    <>
      {/* Chat Trigger Icon */}
      <span className="" >
        <a href="#" className="header__search" onClick={handleOpen} data-toggle="tooltip" title={translations.title}>
          <span className="position-relative">
            <span className="chat-svg-icon position-relative memo_icon" title={translations.title}></span>
          </span>
        </a>
      </span>

      {/* Chat Modal */}
      <Modal
        show={isOpen}
        onHide={handleClose}
        centered
        aria-labelledby="chatOverlayLabel"
        backdrop="static"
        keyboard={true}
        contentClassName="chatOverlay"
      >
        <Modal.Header>
          <Modal.Title id="chatOverlayLabel">{translations.title}</Modal.Title>
          <Button variant="secondary" onClick={handleClose}>
            <span className="o-close-1"></span>
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div className="chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={`chat-message ${message.sender}`}>
                <div className="message-content">
                  {message.sender === 'bot' ? (
                    <div dangerouslySetInnerHTML={{ __html: marked.parse(message.content) }} />
                  ) : (
                    message.content
                  )}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="chat-message bot typing-message">
                <div className="typing-indicator">
                  {translations.typingIndicator}
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            {error && (
              <div className="chat-message bot">
                <div className="message-content text-danger">
                  {error}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input-container">
            <div className="input-group align-items-center">
              <input
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder={translations.inputPlaceholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <button 
                className="btn btn-primary py-2" 
                type="button"
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <span>➤</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ChatOverlay; 